import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

const EMPTY = Object.freeze({ loading: true, errors: {}, classic: null, history: null, suppressions: null, watch: null, guardians: null, watchSuppressions: null });

/**
 * One load for the Notifications tabs. `canWatch` is false for an operator account: it asks for the Watch
 * health (which the portal allows) and never for Watch content (which the portal refuses). A failed part sets
 * errors[part] and leaves the others usable.
 */
export function useNotificationsData(customerId, { canWatch }) {
  const [state, setState] = useState(EMPTY);
  const reqRef = useRef(0);

  const reload = useCallback(async () => {
    const req = ++reqRef.current;
    const calls = {
      classic: () => api.listNotifications(customerId),
      history: () => api.listNotificationHistory(customerId),
      suppressions: () => api.listSuppressions(customerId),
      watch: () => api.getWatch(customerId),
      ...(canWatch ? { guardians: () => api.listWatchGuardians(customerId), watchSuppressions: () => api.listWatchSuppressions(customerId) } : {}),
    };
    const keys = Object.keys(calls);
    const settled = await Promise.allSettled(keys.map((k) => calls[k]()));
    if (reqRef.current !== req) return;
    const next = { ...EMPTY, loading: false, errors: {} };
    keys.forEach((k, i) => {
      const r = settled[i];
      if (r.status === 'fulfilled') next[k] = r.value;
      else next.errors[k] = r.reason?.message || 'could not be loaded';
    });
    setState(next);
  }, [customerId, canWatch]);

  useEffect(() => {
    setState(EMPTY);
    reload();
    return () => { reqRef.current += 1; };
  }, [reload]);

  /** Writes the Notifications master switch; returns { notificationsEnabled, propagated, live }. */
  const setMaster = useCallback(async (enabled) => {
    const out = await api.setNotificationsOptIn(customerId, enabled);
    await reload();
    return out;
  }, [customerId, reload]);

  return { ...state, reload, setMaster };
}
